import { ApolloQueryResult, FetchResult, useQuery } from '@apollo/client';

import { REFETCH_ADMIN_CATALOG } from '@/store/catalog';
import { HookResult } from './apolloClient';
import { Learning, LearningLinks } from '@/types/learning';
import { ADMIN_ROUTE_PATH } from '@/administration/constants/adminRoutePaths';
import { FEATURE, useFeatureEnabled } from '@/feature-toggles';
import { ROUTE_PATH } from '@/constants/routePaths';
import {
  LearningDocument,
  useArchiveLearningMutationRemote,
  useDeleteLearningMutationRemote,
  useRestoreLearningMutationRemote,
} from '@/store/v2';

export type LearningWithLinks = Learning & { links: LearningLinks };

/** What the archive/restore/delete documents actually select. No caller reads it today. */
type MutatedLearning = { id: number };

function getLearningLinks(learning: Learning): LearningLinks {
  return {
    link: `${ROUTE_PATH.LEARNING_BASE}/${learning.id}/${learning.title}`,
    editLink: `${ADMIN_ROUTE_PATH.CATALOG}/${learning.type.toLowerCase().replace(/_/g, '-')}/${learning.id}`,
    previewLink: `${ROUTE_PATH.LEARNING_BASE}/${learning.id}/${learning.title}`,
  };
}

interface LearningResponse extends HookResult {
  learning: LearningWithLinks | null;
  refetch: (variables?: { id?: number | string }) => Promise<ApolloQueryResult<{ learning: Readonly<Learning> }>>;
}

export const useLearning = ({ id, skip = false }: { id: number | string; skip?: boolean }): LearningResponse => {
  const { data, error, refetch, networkStatus, loading } = useQuery<{ learning: Readonly<Learning> }>(LearningDocument, {
    skip,
    variables: { id },
  });

  const learning: LearningWithLinks | null = data?.learning ? { ...data.learning, links: getLearningLinks(data.learning) } : null;

  return {
    learning,
    error,
    refetch,
    networkStatus,
    loading: !learning ? loading : false,
  };
};

export const useArchiveLearning = (): {
  archiveLearning: (learningId: number) => Promise<FetchResult<{ archiveLearning?: MutatedLearning }>>;
  loading: boolean;
} => {
  const [mutate, { loading }] = useArchiveLearningMutationRemote();

  return {
    archiveLearning: (learningId: number) =>
      mutate({
        variables: { learningId },
        refetchQueries: [
          {
            query: LearningDocument,
            variables: { id: learningId },
          },
        ],
      }),
    loading,
  };
};

export const useRestoreLearning = (): {
  restoreLearning: (learningId: number) => Promise<FetchResult<{ restoreLearning?: MutatedLearning }>>;
  loading: boolean;
} => {
  const [mutate, { loading }] = useRestoreLearningMutationRemote();

  return {
    restoreLearning: (learningId: number) =>
      mutate({
        variables: { learningId },
        refetchQueries: [
          {
            query: LearningDocument,
            variables: { id: learningId },
          },
        ],
      }),
    loading,
  };
};

export const useDeleteLearning = (): {
  deleteLearning: (learningId: number) => Promise<FetchResult<{ deleteLearning?: MutatedLearning }>>;
  loading: boolean;
} => {
  const isCatalogJSONResponseEnabled = useFeatureEnabled(FEATURE.SYSTEM_CATALOG_GQL_JSON_RESPONSE_ENABLED);
  const [mutate, { loading }] = useDeleteLearningMutationRemote();

  return {
    deleteLearning: (learningId: number) =>
      mutate({
        variables: { learningId },
        refetchQueries: [REFETCH_ADMIN_CATALOG(isCatalogJSONResponseEnabled)],
      }),
    loading,
  };
};
